import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PublicLayout, PageContainer, PageHeader } from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Sparkle } from "@phosphor-icons/react";

export default function Analysis() {
  useSeo({ title: "Guwahati Morning Teer Analysis | Daily Number Trends", description: "Guwahati Morning Teer analysis with daily auto-updating numbers and result trends. For informational purposes only.", path: "/analysis" });
  const q = useQuery({ queryKey: ["results", "analysis"], queryFn: async () => (await api.get("/results?limit=90")).data });
  const gutiQ = useQuery({ queryKey: ["daily-guti"], queryFn: async () => (await api.get("/daily/guti")).data });

  const { chartData, stats } = useMemo(() => {
    const data = [...(q.data || [])].reverse().map((r) => ({
      date: r.date?.slice(5),
      "F/R": Number(r.fr) || null,
      "S/R": Number(r.sr) || null,
    }));
    const nums = (q.data || []).flatMap((r) => [Number(r.fr), Number(r.sr)]).filter((n) => !isNaN(n));
    return {
      chartData: data,
      stats: {
        total: q.data?.length || 0,
        avg: nums.length ? Math.round(nums.reduce((a, b) => a + b, 0) / nums.length) : 0,
      },
    };
  }, [q.data]);

  return (
    <PublicLayout>
      <PageContainer className="py-10">
        <PageHeader eyebrow="Trends" title="Analysis" subtitle="Auto-updating daily numbers and a visual overview of published results. Informational only." />

        {/* Daily auto guti */}
        <div className="rounded-2xl bg-gradient-to-br from-brand-900 to-brand text-white p-5 sm:p-6 mb-8" data-testid="analysis-daily-guti">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle className="w-5 h-5 text-amber-300" weight="fill" />
            <h3 className="font-heading font-bold text-lg">Today's Analysis Guti</h3>
            <span className="ml-auto text-xs font-semibold text-violet-200">{gutiQ.data?.date}</span>
          </div>
          {gutiQ.isLoading ? (
            <p className="text-violet-200 text-sm">Loading…</p>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {(gutiQ.data?.analysis || []).map((n, i) => (
                <span key={i} data-testid={`guti-num-${i}`} className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/15 font-heading font-black text-xl tabular-nums">
                  {n}
                </span>
              ))}
            </div>
          )}
          <p className="text-[11px] text-violet-200/80 mt-4">These numbers change automatically every day and are for informational / entertainment reference only — not a prediction.</p>
        </div>

        {q.isLoading ? (
          <LoadingState />
        ) : q.isError ? (
          <ErrorState onRetry={q.refetch} />
        ) : chartData.length === 0 ? (
          <EmptyState title="No data to analyze" subtitle="Statistics appear once results are published." />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8">
              {[
                { label: "Records", value: stats.total },
                { label: "Average", value: stats.avg },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl bg-white border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] p-4 text-center" data-testid={`analysis-stat-${i}`}>
                  <p className="font-heading font-black text-2xl sm:text-3xl text-brand tabular-nums">{s.value}</p>
                  <p className="text-xs font-semibold text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] p-4 sm:p-6" data-testid="analysis-chart">
              <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">Result Trend</h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} domain={[0, 100]} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                    <Line type="monotone" dataKey="F/R" stroke="#4C1D95" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="S/R" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </PageContainer>
    </PublicLayout>
  );
}
